/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import moment from 'moment';
import { useQueries, useQuery } from '@tanstack/react-query';
import { labsQuery, questionnairesQuery } from 'pages/PatientRecords/PatientRecords';
import { descend, isEmpty, pluck, prop, sortWith, uniqBy } from 'ramda';
import { LAB_TEST_STATUS, VISIT_STATUS, visitTypes } from 'context/visitContext';
import Graphic from 'components/Graphic/Graphic';
import VisitItem from 'components/VisitItem/VisitItem';
import { LabTest, LabType, Patient, Prescription, QuestionAnswers } from 'types/types';
import { PAGE_TABS } from 'components/VisitRecords/VisitRecords';
import { useLocation, useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import Accordion from 'components/Accordian/Accordian';
import SymptomView from 'components/SymptomView/SymptomView';
import numberToWords from 'utils/numberToWords';
import { formatArrayIntoSentence } from 'utils/utils';
import Icon from 'components/Icon/Icon';
import { paths } from 'routes';
import ChatView from 'components/ChatView/ChatView';
import { usePatient } from '../../context/appContext';

function VisitHistory() {
  const { patient } = usePatient();
  const [visitLimitCount, setVisitLimitCount] = React.useState(10);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = React.useState<QuestionAnswers>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, isFetching } = useQuery({
    ...questionnairesQuery({ patientId: patient.id, status: VISIT_STATUS.COMPLETE }),
    enabled: !!(isEmpty(patient?.questionnaires) && patient?.id),
  });

  const visits = React.useMemo(() => {
    const fQuestionnaires = (data || patient?.questionnaires || [])?.filter(
      fndQuestion => fndQuestion.status === VISIT_STATUS.COMPLETE,
    ) as QuestionAnswers[];

    return fQuestionnaires;
  }, [data, patient?.questionnaires]);

  React.useEffect(() => {
    if (selectedQuestionnaire) {
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, [selectedQuestionnaire]);

  const getVisitTitle = (type: string) => {
    const findVisit = visitTypes.find(fndVisit => fndVisit.type === type.toLocaleUpperCase());
    if (!findVisit) return type;
    return `${findVisit.name}`;
  };

  const handleVisitClicked = (questionnaire: QuestionAnswers) => {
    const visit = visitTypes.find(
      fndVisit => fndVisit.type === questionnaire.type?.toLocaleUpperCase(),
    );

    setSelectedQuestionnaire({
      ...questionnaire,
      visit,
    });
  };

  const handleNextTab = () => {
    const tab = 'visit-history';

    const indexOfActiveTab = PAGE_TABS.findIndex(fndTab => fndTab.id === tab);

    const nextTab = PAGE_TABS.find((_, index) => index === indexOfActiveTab + 1);

    if (nextTab) {
      const tabURL = `${location.pathname.replace(tab, nextTab.id)}${location.search}`;
      navigate(tabURL);
    }
  };

  const renderLoading = () => (
    <div className="flex flex-col space-y-2">
      {new Array(4).fill(null).map((_, index) => (
        <div
          className="laptop:grid laptop:grid-cols-7 gap-6 justify-between py-1 items-center"
          key={index}
        >
          <div className="col-span-2">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div>
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div>
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div className="hidden laptop:block">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
          <div className="col-span-2 hidden laptop:block">
            <Skeleton width="100%" height={25} duration={1.4} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="flex flex-col pt-0 pb-3 tablet:pt-5 tablet:pb-1">
        {!selectedQuestionnaire ? (
          <div>
            {(isLoading && isFetching) || !visits ? (
              <>{renderLoading()}</>
            ) : visits.length === 0 ? (
              <div className="flex flex-col justify-center items-center mt-8 mb-20">
                <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
                <div className="text-center mt-3">
                  <p className="font-medium">You’re all caught up.</p>
                  <p className="text-[#7A7A7A] text-xs">
                    {`${patient.firstName} ${patient.lastName}`} has no listed previous visits.
                  </p>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[#e9ecef]">
                {visits.slice(0, visitLimitCount).map(visit => (
                  <VisitItem
                    key={visit.id}
                    date={moment(visit.createdAt, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                    iconType={visit.type.toLocaleLowerCase()}
                    status={undefined}
                    title={getVisitTitle(visit.type)}
                    onVisitClicked={() => handleVisitClicked(visit)}
                  />
                ))}
              </div>
            )}

            {visits.length > 6 && visitLimitCount === 10 && (
              <div className="text-center pb-1 pt-3 text-river-blue">
                <button
                  onClick={() => setVisitLimitCount(visits.length)}
                  type="button"
                  className="p-2 !font-medium"
                >
                  Show more history...
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex flex-col tablet:flex-row tablet:space-x-4 tablet:mb-2 relative">
              <h3 className="font-medium inline-flex mobile:text-lg">
                <span className="hidden tablet:block w-[5px] h-[5px] rounded-full bg-black self-center mr-1" />
                {selectedQuestionnaire.visit?.name} Visit
              </h3>
              <span className="font-medium inline-flex mobile:text-neutral-400 mobile:text-xs">
                <span className="hidden tablet:block w-[5px] h-[5px] rounded-full bg-black self-center mr-1" />
                {moment(selectedQuestionnaire.createdAt, 'YYYY-MM-DD').format('MM/DD/YYYY')}
              </span>

              <button
                onClick={() => setSelectedQuestionnaire(undefined)}
                type="button"
                className="mobile:top-0 bg-white self-end tablet:flex flex-row justify-center items-center absolute bottom-0 right-0
                w-[35px] h-[35px] tablet:w-10 tablet:h-10 rounded-full shadow-[0_0_12px_rgba(0,0,0,0.1)] hover:opacity-80 "
              >
                <Icon
                  id="close"
                  className="text-river-black w-[12px] h-[12px] tablet:w-[13px] tablet:h-[13px] mx-auto"
                />
              </button>
            </div>

            <VisitOverview patient={patient} questionnaire={selectedQuestionnaire} />
          </div>
        )}
      </div>

      {!(isFetching && isLoading) && (
        <button
          type="button"
          onClick={() => handleNextTab()}
          className="w-fit border border-river-blue text-river-blue rounded-full px-5 py-[9px] text-sm mt-6 min-w-[180px] 
            hover:bg-[#EEFBFF]"
        >
          Next
        </button>
      )}
    </>
  );
}

export default VisitHistory;

function VisitOverview({
  patient,
  questionnaire,
}: {
  patient: Patient;
  questionnaire: QuestionAnswers;
}) {
  return (
    <div className="mb-5">
      <Accordion
        open={false}
        id="symptoms"
        title={({ isOpen }) => {
          return (
            <div className="flex flex-row justify-between w-full">
              <span className="text-neutral-500 font-medium">SYMPTOMS</span>
              <span className="text-river-blue inline-flex items-center text-xs font-medium">
                {isOpen ? 'CLOSE' : 'VIEW ALL'}
              </span>
            </div>
          );
        }}
      >
        {questionnaire.visit && (
          <SymptomView
            visitId={questionnaire.id}
            visit={questionnaire.visit}
            questionnaires={[questionnaire]}
            onDoneLoading={() => false}
          />
        )}
      </Accordion>

      <Accordion
        open={false}
        id="treatment-plan"
        title={({ isOpen }) => {
          return (
            <div className="flex flex-row justify-between w-full">
              <span className="text-neutral-500 font-medium">TREATMENT PLAN</span>
              <span className="text-river-blue inline-flex items-center text-xs font-medium">
                {isOpen ? 'CLOSE' : 'VIEW ALL'}
              </span>
            </div>
          );
        }}
      >
        <TreatmentPlanView questionnaireId={questionnaire.id} questionnaires={[questionnaire]} />
      </Accordion>

      <Accordion
        open={false}
        id="messages"
        title={({ isOpen }) => {
          return (
            <div className="flex flex-row justify-between w-full">
              <span className="text-neutral-500 font-medium">CHAT (MESSAGES)</span>
              <span className="text-river-blue inline-flex items-center text-xs font-medium">
                {isOpen ? 'CLOSE' : 'VIEW ALL'}
              </span>
            </div>
          );
        }}
      >
        <ChatView patient={patient} visitId={questionnaire.id} />
      </Accordion>
    </div>
  );
}

function TreatmentPlanView({
  questionnaireId,
  questionnaires,
}: {
  questionnaireId: string;
  questionnaires: QuestionAnswers[];
}) {
  const treatmentsQueries = useQueries({
    queries: [
      {
        ...questionnairesQuery({ id: questionnaireId }),
        enabled: !!(isEmpty(questionnaires) && questionnaireId),
      },
      {
        ...labsQuery({ questionnaireId }),
        enabled: !!questionnaireId,
      },
    ],
  });

  const isLoading = treatmentsQueries.some(fndQuery => fndQuery.isLoading && fndQuery.isFetching);

  const { questionnaire, labs } = React.useMemo(() => {
    const findQuestionnaire = (
      (treatmentsQueries[0]?.data || questionnaires || []) as QuestionAnswers[]
    ).find(fndQuestionnaire => fndQuestionnaire.id === questionnaireId);

    const findLabs = sortWith(
      [descend(prop('createdAt') as any)],
      (treatmentsQueries[1]?.data || []) as LabTest[],
    );

    return { questionnaire: findQuestionnaire, labs: findLabs };
  }, [questionnaires, treatmentsQueries, questionnaireId]);

  const prescriptions = (questionnaire?.prescriptions || []) as Prescription[];

  const notesForPatient = questionnaire?.notesForPatient || '';

  const notesForGuide = questionnaire?.notesForGuide || '';

  const getMedicationQty = (fndPresc: Prescription) => {
    if (fndPresc.numberDispensed && fndPresc.daysSupply && fndPresc.dispenseUnit) {
      return `${fndPresc.numberDispensed} ${fndPresc.dispenseUnit.toLocaleLowerCase()} / ${
        fndPresc.daysSupply || 0
      } ${(fndPresc.daysSupply || 0) > 1 ? 'days' : 'day'}`;
    }

    if (
      (!fndPresc.dose || fndPresc.dose === '-' || fndPresc.dose === '?') &&
      (!fndPresc.frequency || fndPresc.frequency === '-' || fndPresc.frequency === '?')
    ) {
      return '-';
    }

    const dose = `${fndPresc.dose}${
      fndPresc.numberToBeTaken
        ? `, ${numberToWords(parseInt(fndPresc.numberToBeTaken, 10))} (${fndPresc.numberToBeTaken})`
        : ''
    }`;

    const frequency = `, ${fndPresc.frequency}`;

    return `${dose} ${frequency}`;
  };

  const getLabTypes = (types: LabType[]) => {
    const names = pluck('name', uniqBy(prop('name'), types) as LabType[]);
    return formatArrayIntoSentence(names);
  };

  const getLabStatus = (status: string) => {
    return status === LAB_TEST_STATUS.RECEIVED_REPORT ? 'View results' : 'Pending';
  };

  const renderLoading = () => (
    <div className="flex flex-col space-y-8">
      <div className="border-[1.8px] border-[#E0E4E7] rounded-lg px-7 pt-4 pb-7">
        <h3 className="font-medium text-[17px]">Prescription</h3>
        <hr className="border-b-[1.5px] border-t-0 border-[#e9ecef] mt-3 mb-4" />

        {new Array(1).fill(null).map((_, index) => (
          <div className="flex flex-col" key={index}>
            <div className="w-full">
              <div className="flex flex-col tablet:flex-row space-x-6">
                <div className="flex-1">
                  <h4 className="text-[15px] text-zinc-400 -mb-1 opacity-60">Medication name</h4>
                  <p className="font-medium">
                    <Skeleton width="100%" height={23} duration={1.4} />
                  </p>
                </div>
                <div className="w-full tablet:w-[25%]">
                  <h4 className="text-[15px] text-zinc-400 -mb-1 opacity-60">Quantity</h4>
                  <p className="font-medium">
                    <Skeleton width="100%" height={23} duration={1.4} />
                  </p>
                </div>
                <div className="w-full tablet:w-[25%]">
                  <h4 className="text-[15px] text-zinc-400 -mb-1 opacity-60">Date</h4>
                  <p className="font-medium">
                    <Skeleton width="100%" height={23} duration={1.4} />
                  </p>
                </div>
              </div>
            </div>

            <div className="block w-full tablet:w-10/12 mt-2">
              <h4 className="text-[15px] text-zinc-400 -mb-1 opacity-60">Instruction</h4>
              <p className="font-medium">
                <Skeleton width="100%" height={20} duration={1.4} />
                <Skeleton width="100%" height={20} duration={1.4} />
                <Skeleton width="80%" height={20} duration={1.4} />
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="border-[1.8px] border-[#E0E4E7] rounded-lg px-7 pt-4 pb-7">
        <h3 className="font-medium text-[17px]">Labs</h3>
        <hr className="border-b-[1.5px] border-t-0 border-[#e9ecef] mt-3 mb-4" />

        {new Array(3).fill(null).map((_, index) => (
          <div
            className="laptop:grid laptop:grid-cols-7 gap-6 justify-between py-1 items-center"
            key={index}
          >
            <div className="col-span-4">
              <Skeleton width="100%" height={23} duration={1.4} />
            </div>
            <div>
              <Skeleton width="90%" height={23} duration={1.4} />
            </div>
          </div>
        ))}
      </div>

      <div className="border-[1.8px] border-[#E0E4E7] rounded-lg px-7 pt-6 pb-7 flex flex-col tablet:flex-row space-y-5 tablet:space-y-0">
        <div className="flex-1">
          <h3 className="font-medium text-[17px]">Provider notes</h3>
          <div className="mt-2">
            <Skeleton width="100%" height={20} duration={1.4} />
            <Skeleton width="100%" height={20} duration={1.4} />
            <Skeleton width="80%" height={20} duration={1.4} />
          </div>
        </div>

        <hr className="hidden tablet:block border-r-[1.5px] border-[#e9ecef] ml-7 mr-11 h-auto" />

        <div className="flex-1">
          <h3 className="font-medium text-[17px]">Notes to patient</h3>
          <div className="mt-2">
            <Skeleton width="100%" height={20} duration={1.4} />
            <Skeleton width="100%" height={20} duration={1.4} />
            <Skeleton width="80%" height={20} duration={1.4} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {isLoading ? (
        renderLoading()
      ) : (
        <>
          <div className="border-[1.8px] border-[#E0E4E7] rounded-lg px-7 pt-4 pb-7">
            <h3 className="font-medium text-[17px]">Prescription</h3>
            <hr className="border-b-[1.5px] border-t-0 border-[#e9ecef] mt-3 mb-4" />

            {prescriptions.length === 0 ? (
              <div className="text-neutral-400">No Prescriptions.</div>
            ) : (
              <div className="flex flex-col w-full space-y-5">
                {prescriptions.map(prescription => (
                  <div
                    className="flex flex-row"
                    key={prescription.prescriptionPhotonId || prescription.id}
                  >
                    {prescriptions.length > 1 && (
                      <span className="w-[6px] h-[6px] rounded-full bg-black self-center mr-5 shrink-0" />
                    )}

                    <div className="w-full">
                      <div className="flex flex-col tablet:flex-row">
                        <div className="flex-1">
                          <h4 className="text-[15px] text-zinc-400">Medication name</h4>
                          <p className="font-medium">{prescription.medicationName}</p>
                        </div>
                        <div className="w-full tablet:w-[25%]">
                          <h4 className="text-[15px] text-zinc-400">Quantity</h4>
                          <p className="font-medium">{getMedicationQty(prescription)}</p>
                        </div>
                        <div className="w-full tablet:w-[25%]">
                          <h4 className="text-[15px] text-zinc-400">Date</h4>
                          <p className="font-medium">
                            {moment(prescription.createdAt).format('MM/DD/YYYY')}
                          </p>
                        </div>
                      </div>
                      <div className="w-full tablet:w-10/12">
                        <h4 className="text-[15px] text-zinc-400">Instruction</h4>
                        <p className="font-medium">{prescription.instructions}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-[1.8px] border-[#E0E4E7] rounded-lg px-7 pt-4 pb-7">
            <h3 className="font-medium text-[17px]">Labs</h3>
            <hr className="border-b-[1.5px] border-t-0 border-[#e9ecef] mt-3 mb-4" />

            {labs.length === 0 ? (
              <div className="text-neutral-400">No Labs.</div>
            ) : (
              <div className="flex flex-row w-full space-x-10">
                <div className="flex flex-col space-y-4 tablet:space-y-2 w-full tablet:w-1/2">
                  {labs.map(lab => (
                    <div>
                      {getLabTypes(lab.types)}

                      {getLabStatus(lab.status) === 'Pending' ? (
                        <>&nbsp; - {getLabStatus(lab.status)}</>
                      ) : (
                        <a
                          className={`inline-flex tablet:hidden whitespace-pre ${
                            getLabStatus(lab.status) === 'Pending'
                              ? 'text-neutral-400'
                              : 'text-river-blue font-medium'
                          }`}
                          target="_blank"
                          href={`${paths.LAB_RESULTS_PATH.replace(':labTestId', lab.id || '')}`}
                          rel="noreferrer"
                        >
                          &nbsp; - {getLabStatus(lab.status)}
                        </a>
                      )}
                    </div>
                  ))}
                </div>

                <div className="hidden tablet:flex flex-col space-y-2 items-end text-[15px]">
                  {labs.map(lab => (
                    <div
                      className={`${
                        getLabStatus(lab.status) === 'Pending'
                          ? 'text-neutral-400'
                          : 'text-river-blue font-medium'
                      }`}
                    >
                      {getLabStatus(lab.status) === 'Pending' ? (
                        getLabStatus(lab.status)
                      ) : (
                        <a
                          target="_blank"
                          href={`${paths.LAB_RESULTS_PATH.replace(':labTestId', lab.id || '')}`}
                          rel="noreferrer"
                        >
                          {getLabStatus(lab.status)}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-[1.8px] border-[#E0E4E7] rounded-lg px-7 pt-6 pb-7 flex flex-col tablet:flex-row space-y-5 tablet:space-y-0">
            <div className="flex-1">
              <h3 className="font-medium text-[17px]">Provider notes</h3>
              <div>{notesForGuide || '-'}</div>
            </div>

            <hr className="hidden tablet:block border-r-[1.5px] border-[#e9ecef] ml-7 mr-11 h-auto" />

            <div className="flex-1">
              <h3 className="font-medium text-[17px]">Notes to patient</h3>
              <div>{notesForPatient || '-'}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
